import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { LeaveService } from "@/services/Leave/LeaveSetting";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ThrowError } from "@/api/callApi";
import { GetFilterUrl, showToastNotification } from "@/utility/helper";
import type { IFilter, IPageInfo } from "@/interface/common.interface";
import TablePagination from "@/components/common/TablePagination";
import type { TableColumn } from "@/components/common/CommonTable";
import { Button } from "@/components/ui/button";
import { RoutesLink } from "@/utility/RouteLinks";
import {
  Pencil,
  Plus,
  Trash,
  TriangleAlert,
  MoreHorizontal,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import CommonTable from "@/components/common/CommonTable";
import CommonModal from "@/components/common/CommonModal";
import type { ILeaveSetting } from "@/interface/leave";
import moment from "moment";
import CommonFilter from "@/components/common/CommonFilter";

const TYPE_OPTIONS = [
  { label: "All Types", value: "all" },
  { label: "Causal Leave", value: "Causal Leave" },
  { label: "Sick Leave", value: "Sick Leave" },
];

const LeaveSettings: React.FC = () => {
  const navigate = useNavigate();
  const [leaveSettings, setLeaveSettings] = useState<ILeaveSetting[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    type: "",
  });

  const handleFilterChange = useCallback((value: string, name: string) => {
    setFilter((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSearchChange = (value: string) => {
    setFilter((prev) => {
      if (prev.search === value) return prev;
      return { ...prev, search: value };
    });
    setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
  };

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const handleGetLeaveSettings = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    setIsLoading(true);
    try {
      const apiFilter = {
        ...filter,
      };
      const paramsURL = GetFilterUrl(apiFilter);
      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""}`;
      const response = await LeaveService.getLeaveSettings(PATH);
      if (response?.status) {
        const settings = response?.data?.leaveSettings || [];
        setLeaveSettings(settings);
        setPaginationData((prev) => ({
          ...prev,
          total: response?.data?.pageInfo?.total || 0,
          totalPages: response?.data?.pageInfo?.totalPages || 0,
        }));
      }
    } catch (error) {
      ThrowError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteLeaveSetting = async () => {
    try {
      const response = await LeaveService.deleteLeaveSetting(deleteId);
      if (response?.status) {
        showToastNotification("success", response?.message);
        toggleDeleteDialog("");
        handleGetLeaveSettings();
      } else {
        showToastNotification("danger", response?.message);
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  useEffect(() => {
    handleGetLeaveSettings(paginationData.currentPage, paginationData.pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationData.currentPage,
    paginationData.pageSize,
    filter,
    filter?.type,
  ]);

  const toggleDeleteDialog = (id: string = "") => {
    setDeleteId(id ?? "");
    setDeleteDialogOpen(!deleteDialogOpen);
  };

  const columns: TableColumn<ILeaveSetting>[] = [
    {
      key: "srNo",
      header: "Sr No",
      render: (_item: ILeaveSetting, index: number) => (
        <span className="text-muted-foreground font-medium"># {index + 1}</span>
      ),
    },
    {
      key: "type",
      header: "Leave Type",
      render: (item: ILeaveSetting) => (
        <span className="font-semibold capitalize">{item?.type}</span>
      ),
    },
    {
      key: "applicableFrom",
      header: "Applicable From",
      render: (item: ILeaveSetting) => (
        <span className="font-medium">
          {item?.applicableFrom
            ? moment(item.applicableFrom).format("DD-MM-YYYY")
            : "—"}
        </span>
      ),
    },
    {
      key: "totalLeavePerMonth",
      header: "Leave/Month",
      render: (item: ILeaveSetting) => (
        <span className="font-medium">{item?.totalLeavePerMonth}</span>
      ),
    },
    {
      key: "totalLeavePerYear",
      header: "Leave/Year",
      render: (item: ILeaveSetting) => (
        <span className="font-medium">{item?.totalLeavePerYear}</span>
      ),
    },
    {
      key: "monthlyCarryForward",
      header: "Monthly Carry Forward",
      render: (item: ILeaveSetting) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.monthlyCarryForward ? "text-blue-600 border-blue-600 bg-blue-50" : "text-slate-500 border-slate-300 bg-slate-50"}`}
        >
          {item?.monthlyCarryForward ? "Yes" : "No"}
        </span>
      ),
    },
    {
      key: "yearCarryForward",
      header: "Yearly Carry Forward",
      render: (item: ILeaveSetting) => (
        <span
          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.yearCarryForward ? "text-blue-600 border-blue-600 bg-blue-50" : "text-slate-500 border-slate-300 bg-slate-50"}`}
        >
          {item?.yearCarryForward ? "Yes" : "No"}
        </span>
      ),
    },
    // {
    //     key: 'status',
    //     header: 'Status',
    //     render: (item: ILeaveSetting) => (
    //         <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full ${item?.isActive ? 'text-green-600 border-green-600 bg-green-50' : 'text-destructive border-destructive bg-destructive/10'}`}>
    //             {item?.isActive ? 'Active' : 'Inactive'}
    //         </span>
    //     )
    // },
    {
      key: "actions",
      header: "Actions",
      render: (item: ILeaveSetting) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="">
              <span className="sr-only">Open menu</span>
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="min-w-48 rounded-xl shadow-lg border-slate-100"
          >
            <DropdownMenuItem
              onClick={() =>
                navigate(`${RoutesLink.editLeaveSetting}/${item.id}`)
              }
              className="cursor-pointer "
            >
              <Pencil className="h-4 w-4" />
              Edit Setting
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => toggleDeleteDialog(item.id)}
              className="cursor-pointer text-red-500"
            >
              <Trash className="h-4 w-4" />
              Delete Setting
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading
          title="Leave Settings"
          buttonText="Add Leave Setting"
          buttonIcon={<Plus size={18} />}
          onClick={() => navigate(RoutesLink.addLeaveSetting)}
        />

        <CommonFilter
          searchValue={filter.search || ""}
          onChange={handleFilterChange}
          onSearchChange={handleSearchChange}
          sortName="type"
          sortPlaceholder="All Types"
          sortOptions={TYPE_OPTIONS}
        />

        <CommonTable
          columns={columns}
          data={leaveSettings}
          loading={isLoading}
        />
        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />

        <CommonModal
          show={deleteDialogOpen}
          onClose={() => toggleDeleteDialog("")}
          title=""
          className="max-w-md rounded-2xl p-0 overflow-hidden"
        >
          <div className="p-6">
            <div className="flex flex-col items-center text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
                <TriangleAlert className="text-red-600 w-8 h-8" />
              </div>
              <h2 className="mt-5 text-2xl font-bold text-gray-900">
                Delete Leave Setting?
              </h2>
              <p className="mt-3 text-sm leading-6 text-gray-500">
                This action cannot be undone. Once deleted, the leave setting
                will be permanently revoked.
              </p>
            </div>
            <div className="mt-8 flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => toggleDeleteDialog()}
              >
                Cancel
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDeleteLeaveSetting}
              >
                Delete
              </Button>
            </div>
          </div>
        </CommonModal>
      </div>
    </Layout>
  );
};

export default LeaveSettings;
